import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  HouseholdRetirementService, type HouseholdModel, type Inputs, type ProjectResponse, type Simulation,
} from '../../services/household-retirement-service';
import { AssumptionsEditor } from './AssumptionsEditor';
import { CashFlowTable } from './CashFlowTable';
import { DebtsPanel } from './DebtsPanel';
import { ExpensesEditor } from './ExpensesEditor';
import { InputsPanel } from './InputsPanel';
import { MilestonesTimeline } from './MilestonesTimeline';
import { NetWorthBandChart } from './NetWorthBandChart';
import { PeopleEditor } from './PeopleEditor';
import { ReadinessCard } from './ReadinessCard';
import { ScopePanel } from './ScopePanel';
import { money, monthYear } from './format';

const errMsg = (e: any, fallback: string) => e?.response?.data?.message ?? e?.message ?? fallback;

/**
 * The FIRE page's "Household plan" tab (issue #32): an opt-in, versioned
 * household model — people, incomes and leave, life-stage costs, debts and
 * assumptions — projected deterministically ("everything goes well") and
 * through the Monte Carlo engine, with readiness at retirement.
 *
 * Edits preview immediately (the unsaved model rides in the request body);
 * nothing is stored until "Save version". A projection, not advice.
 */
export const HouseholdPlanTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [version, setVersion] = useState(0);
  const [model, setModel] = useState<HouseholdModel | null>(null);
  const [inputs, setInputs] = useState<Inputs | null>(null);
  const [disclaimer, setDisclaimer] = useState('');
  const [dirty, setDirty] = useState(false);
  const [proj, setProj] = useState<ProjectResponse | null>(null);
  const [sim, setSim] = useState<Simulation | null>(null);
  const [running, setRunning] = useState(false);
  const [simRunning, setSimRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState<string>('household');
  const scopeRef = useRef('household');
  const runId = useRef(0);
  const debounce = useRef<number | undefined>(undefined);

  const run = useCallback(async (m: HouseholdModel | undefined) => {
    const id = ++runId.current;
    setRunning(true);
    setSimRunning(true);
    setError(null);
    try {
      const sc = scopeRef.current;
      const p = await HouseholdRetirementService.project(m, sc);
      if (id !== runId.current) return;
      setProj(p);
      setRunning(false);
      const s = await HouseholdRetirementService.simulate(m, sc);
      if (id !== runId.current) return;
      setSim(s.simulation);
    } catch (e: any) {
      if (id === runId.current) setError(errMsg(e, 'Projection failed'));
    } finally {
      if (id === runId.current) {
        setRunning(false);
        setSimRunning(false);
      }
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await HouseholdRetirementService.get(true);
      setConfigured(r.configured);
      setVersion(r.version);
      setModel(r.model ?? r.starter ?? null);
      setInputs(r.inputs ?? null);
      setDisclaimer(r.disclaimer);
      setDirty(false);
      if (r.configured) void run(undefined);
    } catch (e: any) {
      setError(errMsg(e, 'Failed to load the household plan'));
    } finally {
      setLoading(false);
    }
  }, [run]);

  useEffect(() => { void load(); }, [load]);

  const edit = (m: HouseholdModel) => {
    setModel(m);
    setDirty(true);
    setNotice(null);
    window.clearTimeout(debounce.current);
    // Preview once the edits settle; needs at least one adult to project.
    if (m.people.some((p) => p.role === 'adult')) {
      debounce.current = window.setTimeout(() => void run(m), 900);
    }
  };
  useEffect(() => () => window.clearTimeout(debounce.current), []);

  const save = async () => {
    if (!model) return;
    setSaving(true);
    setError(null);
    try {
      const r = await HouseholdRetirementService.save(model);
      setConfigured(true);
      setVersion(r.version);
      setDirty(false);
      setNotice(`Saved as household plan version ${r.version}.`);
      void run(undefined);
    } catch (e: any) {
      setError(errMsg(e, 'Failed to save the plan'));
    } finally {
      setSaving(false);
    }
  };

  const saveAnalysis = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await HouseholdRetirementService.saveAnalysis(dirty && model ? model : undefined, scopeRef.current);
      setNotice(`Saved “${r.title}” to Analyses.`);
    } catch (e: any) {
      setError(errMsg(e, 'Failed to save the analysis'));
    } finally {
      setSaving(false);
    }
  };

  const chooseScope = (sc: string | null) => {
    if (!sc || sc === scopeRef.current) return;
    scopeRef.current = sc;
    setScope(sc);
    setSim(null);
    if (configured || dirty) void run(dirty && model ? model : undefined);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!model) return <Alert severity="error">{error ?? 'No household plan available.'}</Alert>;

  const p = proj?.projection;
  const currency = p?.currency ?? inputs?.currency ?? 'CAD';
  const kpis = p ? [
    { label: 'Net worth today', value: money(p.summary.start_net_worth, currency), sub: `investable ${money(p.summary.start_investable, currency)}` },
    ...p.summary.at_65.map((a) => ({
      label: `${a.name} at 65 · ${monthYear(a.date)}`,
      value: money(a.net_worth, currency),
      sub: `investable ${money(a.investable, currency)} (${money(a.investable_real, currency)} today)`,
    })),
    {
      label: 'Sustainable retirement spending',
      value: sim ? `${money(sim.sustainable_spending, currency)}/yr` : '…',
      sub: sim ? `today's $, ${sim.success_target_pct}% Monte Carlo success` : 'simulating',
    },
  ] : [];

  return (
    <Box data-testid="household-plan">
      <Alert severity="info" sx={{ mb: 2 }} data-testid="household-disclaimer">
        {disclaimer || 'A projection, not financial advice.'} Incomes and dates of birth stay in this household only.
      </Alert>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => chooseScope(v)} aria-label="Plan scope">
          <ToggleButton value="household">Household</ToggleButton>
          {model.people.filter((pp) => pp.role === 'adult').map((pp) => (
            <ToggleButton key={pp.id} value={`person:${pp.id}`}>{pp.name}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Chip size="small" label={configured ? `Version ${version}${dirty ? ' · unsaved edits' : ''}` : 'Not saved yet'}
          color={dirty ? 'warning' : 'default'} />
        {(running || simRunning) && <CircularProgress size={18} aria-label="Calculating" />}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => void run(dirty ? model : undefined)}
          disabled={running || (!configured && !dirty)}>Recalculate</Button>
        <Button variant="outlined" onClick={() => void saveAnalysis()} disabled={saving || (!configured && !dirty) || !p}>
          Save as analysis
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving || (!dirty && configured)}>
          {configured ? 'Save version' : 'Start household plan'}
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {!configured && !dirty && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography color="text.secondary">
              The household plan is opt-in and separate from the simple plans. Add the people in the household, their
              incomes and any leave, then save a version — FolioWorth reads your investable assets, home, debts and last
              12 months of spending live, and projects to age 95.
            </Typography>
          </CardContent>
        </Card>
      )}

      {p && proj && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {kpis.map((k) => (
              <Card key={k.label} sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">{k.label}</Typography>
                  <Typography variant="h5" className="fw-num">{k.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{k.sub}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <ReadinessCard readiness={proj.readiness} summary={p.summary} simulation={sim} currency={currency} />
          <NetWorthBandChart simulation={sim} projection={p} loading={simRunning} currency={currency} />
          {p.warnings.length > 0 && <Alert severity="warning">{p.warnings.join(' ')}</Alert>}
          {p.available_credit && <DebtsPanel projection={p} currency={currency} />}
          {proj.scope && <ScopePanel scope={proj.scope} currency={currency} />}
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}><MilestonesTimeline milestones={p.milestones} /></Box>
            <Box sx={{ flex: 2, minWidth: 0, width: '100%' }}><CashFlowTable years={p.years} /></Box>
          </Stack>
        </Stack>
      )}

      <Accordion defaultExpanded={!configured}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>People, incomes and leave</Typography></AccordionSummary>
        <AccordionDetails><PeopleEditor model={model} onChange={edit} /></AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Spending, children and debts</Typography></AccordionSummary>
        <AccordionDetails><ExpensesEditor model={model} inputs={inputs} onChange={edit} /></AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Assumptions</Typography></AccordionSummary>
        <AccordionDetails><AssumptionsEditor model={model} onChange={edit} /></AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Planned one-off events ({(model.events ?? []).length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={0.5} data-testid="plan-events">
            {(model.events ?? []).map((e) => (
              <Typography key={e.id} variant="body2">
                {monthYear(e.date.length === 7 ? `${e.date}-01` : e.date)} — {e.label}
                {e.amount != null ? ` · ${money(e.amount, currency)}${e.amount_assumed ? ' (estimate)' : ''}` : ''}
              </Typography>
            ))}
            {(model.events ?? []).length === 0 && <Typography variant="body2" color="text.secondary">No events yet.</Typography>}
            <Box><Button size="small" href="/timeline">Add or edit events on the Timeline</Button></Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
      {inputs && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Live data used (read-only)</Typography></AccordionSummary>
          <AccordionDetails><InputsPanel inputs={inputs} /></AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
